import React, { useEffect } from 'react';
import { Translation, getI18n } from 'react-i18next';
import {
  Affix,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Spin,
  Space,
  Button,
} from 'antd';
import moment from 'moment';
import PriorityInput from './PriorityInput';
import SendStatusInput from './SendStatusInput';
import {
  LoadingOutlined,
  MinusCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import message from '../../../elements/lib/MessageWrapper';
import FormMetadata from '../../../elements/components/FormMetadata';
import Format from '../../../../../lib/Format';
import Logger from '../../../../../lib/Logger';
import Config from '../../../../../Config';

const RecipientsInputRow = ({ recipient, remove, index, ...props }) => {
  return (
    <Translation>
      {(t) => (
        <div className='recipient-input-row'>
          <Space style={{ display: 'flex' }} align='start'>
            <div className='form-group'>
              <Form.Item
                {...recipient}
                label={t('smses_recipient')}
                rules={[
                  { type: 'email', message: t('feedback_validation_email') },
                  {
                    required: true,
                    message: t('feedback_validation_required'),
                  },
                ]}
              >
                <Input autoFocus />
              </Form.Item>
            </div>
            <MinusCircleOutlined
              onClick={() => {
                remove(index);
              }}
            />
          </Space>
        </div>
      )}
    </Translation>
  );
};

const SmsForm = ({
  id,
  data,
  errors,
  load,
  destroyForm,
  isLoading,
  isSubmitting,
  ...props
}) => {
  // eslint-disable-next-line no-unused-vars
  const [form] = Form.useForm();

  const apiDatetimeFormat = Config.get('API_DATETIME_FORMAT');

  // form column settings
  const layout = {
    main: {
      labelCol: { span: 5 },
      wrapperCol: { span: 19 },
    },
  };

  // load record data from API
  useEffect(() => {
    if (id) {
      load(id);
    }
  }, [id, load]);

  // update input values when new data is available
  const dataString = JSON.stringify(data);
  useEffect(() => {
    if (id && !isSubmitting) {
      const dataObj = JSON.parse(dataString);
      dataObj['last_attempt_at'] = dataObj['last_attempt_at']
        ? moment(dataObj['last_attempt_at'], apiDatetimeFormat)
        : null;

      dataObj['queued_at'] = dataObj['queued_at']
        ? moment(dataObj['queued_at'], apiDatetimeFormat)
        : moment();
      form.setFieldsValue(dataObj);
    }
  }, [form, dataString, isSubmitting, id, apiDatetimeFormat]);

  // handle errors reported by API
  useEffect(() => {
    let firstFieldName = '';
    for (const field in errors) {
      form.setFields([{ name: field, errors: errors[field] }]);
      if (firstFieldName === '') {
        firstFieldName = field;
      }
    }
    form.scrollToField(firstFieldName);
  }, [form, errors]);

  // submit data handler
  const submitData = async (values) => {
    Logger.log('debug', `SmsForm.submitData(###)`);

    // API POST/PUT payload
    let payload = {};
    for (const input of Object.keys(data)) {
      if (input in values) {
        // transform date values
        if (['queued_at', 'last_attempt_at'].includes(input)) {
          payload[input] = Format.date(values[input], apiDatetimeFormat);
        } else {
          payload[input] = values[input];
        }
      }
    }

    if (id) {
      // update
      props.update(id, payload, (success) => {
        if (success) {
          message.success(getI18n().t('feedback_form_success'));
        } else {
          message.error(getI18n().t('feedback_form_error'));
        }
      });
    }
  };

  // form submit handler
  const handleFinish = async (values) => {
    Logger.log('debug', `SmsForm.handleFinish(###)`);
    if (!props.isSubmitting) {
      await submitData(values);
    }
  };

  // form error handler
  const handleFinishFailed = ({ values, errorFields, outOfDate }) => {
    Logger.log('debug', `SmsForm.handleFinishFailed(###)`);
    message.error(getI18n().t('feedback_form_error'));
    if (errorFields && errorFields.length > 0) {
      form.scrollToField(errorFields[0].name);
    }
  };

  return (
    <Translation>
      {(t) => (
        <>
          <div className='sms-form'>
            <Form
              name='smses_form'
              form={form}
              initialValues={data}
              onFinish={handleFinish}
              onFinishFailed={handleFinishFailed}
              validateTrigger='onSubmit'
              {...layout.main}
            >
              <Row gutter={16}>
                <Col xs={24} lg={18}>
                  <Card
                    title={id ? t('smses_edit_title') : t('smses_add_title')}
                    extra={
                      isLoading ? (
                        <Spin
                          indicator={
                            <LoadingOutlined style={{ fontSize: 20 }} spin />
                          }
                        />
                      ) : null
                    }
                  >
                    <div className='form-group'>
                      <Form.Item
                        name='attempts'
                        label={t('smses_attempts')}
                        rules={[
                          {
                            required: true,
                            message: t('feedback_validation_required'),
                          },
                        ]}
                      >
                        <Input autoFocus disabled={true} />
                      </Form.Item>
                    </div>

                    <div className='form-group'>
                      <Form.Item
                        name='total_count'
                        label={t('smses_total_count')}
                        rules={[
                          {
                            required: true,
                            message: t('feedback_validation_required'),
                          },
                        ]}
                      >
                        <Input autoFocus disabled={true} />
                      </Form.Item>
                    </div>

                    <div className='form-group'>
                      <Form.Item
                        name='total_sent_count'
                        label={t('smses_total_sent_count')}
                        rules={[
                          {
                            required: true,
                            message: t('feedback_validation_required'),
                          },
                        ]}
                      >
                        <Input autoFocus disabled={true} />
                      </Form.Item>
                    </div>

                    <div className='form-group'>
                      <Form.Item
                        name='total_delivered_count'
                        label={t('smses_total_delivered_count')}
                        rules={[
                          {
                            required: true,
                            message: t('feedback_validation_required'),
                          },
                        ]}
                      >
                        <Input autoFocus disabled={true} />
                      </Form.Item>
                    </div>

                    <div className='form-group'>
                      <Form.Item
                        name='total_delivery_failed_count'
                        label={t('smses_total_delivery_failed_count')}
                        rules={[
                          {
                            required: true,
                            message: t('feedback_validation_required'),
                          },
                        ]}
                      >
                        <Input autoFocus disabled={true} />
                      </Form.Item>
                    </div>

                    <div className='form-group'>
                      <Form.Item
                        name='service_id'
                        label={t('smses_service_id')}
                        rules={[]}
                      >
                        <Input autoFocus disabled={isLoading || isSubmitting} />
                      </Form.Item>
                    </div>

                    <div className='form-group'>
                      <Form.Item
                        name='body'
                        label={t('smses_body')}
                        rules={null}
                      >
                        <Input.TextArea
                          rows={5}
                          disabled={isLoading || isSubmitting}
                        />
                      </Form.Item>
                    </div>

                    <PriorityInput
                      name='priority'
                      label={t('smses_priority')}
                      rules={null}
                      disabled={isLoading || isSubmitting}
                    />

                    <SendStatusInput
                      name='send_status'
                      label={t('smses_send_status')}
                      rules={null}
                      disabled={isLoading || isSubmitting}
                    />

                    <div className='form-group'>
                      <Form.Item
                        name='last_attempt_at'
                        label={t('smses_last_attempt_at')}
                        rules={[]}
                      >
                        <DatePicker
                          autoFocus
                          showTime
                          disabled={isLoading || isSubmitting}
                        />
                      </Form.Item>
                    </div>

                    <div className='form-group'>
                      <Form.Item
                        name='queued_at'
                        label={t('smses_queued_at')}
                        rules={[
                          {
                            required: true,
                            message: t('feedback_validation_required'),
                          },
                        ]}
                      >
                        <DatePicker
                          autoFocus
                          showTime
                          disabled={isLoading || isSubmitting}
                        />
                      </Form.Item>
                    </div>
                  </Card>

                  <Card
                    title={t('sms_recipients_title')}
                    extra={
                      isLoading ? (
                        <Spin
                          indicator={
                            <LoadingOutlined style={{ fontSize: 20 }} spin />
                          }
                        />
                      ) : null
                    }
                  >
                    <Form.List name='recipients'>
                      {(recipients, { add, remove, move }) => {
                        return (
                          <>
                            <div className='scroll-recipients'>
                              {recipients.map((recipient, i) => (
                                <RecipientsInputRow
                                  recipient={recipient}
                                  index={i}
                                  remove={remove}
                                />
                              ))}
                            </div>

                            <Form.Item style={{ marginBottom: 0 }}>
                              <Button
                                type='dashed'
                                onClick={() => {
                                  add();
                                }}
                                block
                              >
                                <PlusOutlined /> {t('smses_add_recipient')}
                              </Button>
                            </Form.Item>
                          </>
                        );
                      }}
                    </Form.List>
                  </Card>
                </Col>

                <Col xs={24} lg={6}>
                  <Affix offsetTop={10}>
                    <Card title={t('form_metadata_header')}>
                      <FormMetadata
                        id={id}
                        isSubmitting={isSubmitting}
                        delete={props.delete.bind(this)}
                        deleteRedirectTo='SmsesScreen'
                        createdAt={props.createdAt}
                        showStatus={false}
                        updatedAt={props.updatedAt}
                      />
                    </Card>
                  </Affix>
                </Col>
              </Row>
            </Form>
          </div>
        </>
      )}
    </Translation>
  );
};

export default SmsForm;

Logger.log('silly', `SmsForm loaded.`);
